import React from "react";
import { createRoot } from "react-dom/client";
import { drawLandingIcons } from "./lucide_icons";
import { CONTENT } from "./sections/shared";
import { Header } from "./sections/header";
import { Hero, BuiltFor } from "./sections/hero";
import { Pipeline, Showreel, FeaturesIntro, Showcase, Infrastructure, Capabilities } from "./sections/features";
import { ContentMemory } from "./sections/memory";
import { Formats, CTA, DownloadApp, Mcp, Contact, Footer } from "./sections/closing";

/* Product-tour blocks: anchor + layout (side, dark) per key. The order
   on the page comes from the payload (admin drag-and-drop). */
const SHOWCASE_CONFIG = {
  showcase_video: { id: "video", flip: false, dark: false },
  showcase_locations: { id: "locations", flip: true, dark: false },
  showcase_previz: { id: "previz", flip: false, dark: true },
  showcase_phone: { id: "phone", flip: true, dark: true },
  showcase_script: { id: "script", flip: false, dark: false },
  showcase_assets: { id: "assets", flip: true, dark: false },
  showcase_sketch: { id: "sketch", flip: false, dark: false },
  showcase_agent: { id: "agent", flip: true, dark: true },
  showcase_tasks: { id: "tasks", flip: false, dark: false },
  showcase_review: { id: "review", flip: true, dark: false },
  showcase_animatics: { id: "animatics", flip: false, dark: false },
  showcase_audio: { id: "audio", flip: true, dark: false },
  showcase_canvas: { id: "canvas", flip: false, dark: false },
  showcase_flow: { id: "flow", flip: true, dark: false },
  showcase_billing: { id: "billing", flip: true, dark: true },
  showcase_edit: { id: "edit", flip: false, dark: false },
};

const SECTION_COMPONENTS = {
  hero: Hero,
  built_for: BuiltFor,
  pipeline: Pipeline,
  features_intro: FeaturesIntro,
  showreel: Showreel,
  infrastructure: Infrastructure,
  capabilities: Capabilities,
  mcp: Mcp,
  memory: ContentMemory,
  formats: Formats,
  cta: CTA,
  download: DownloadApp,
  contact: Contact,
};

function Sections() {
  const order = CONTENT.order || Object.keys(CONTENT.blocks || {});
  return order.map((key) => {
    const sc = SHOWCASE_CONFIG[key];
    if (sc) return <Showcase key={key} id={sc.id} blockKey={key} flip={sc.flip} dark={sc.dark} />;
    const Section = SECTION_COMPONENTS[key];
    return Section ? <Section key={key} /> : null;
  });
}

function LandingPage() {
  React.useEffect(() => {
    const draw = () => drawLandingIcons();
    draw();
    const id = setTimeout(draw, 60);
    return () => clearTimeout(id);
  });

  React.useEffect(() => {
    const els = Array.from(document.querySelectorAll(".reveal, .fr-sweep"));
    if (!("IntersectionObserver" in window) || !els.length) {
      els.forEach((el) => el.classList.add("is-in"));
      return;
    }
    const io = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-in");
            obs.unobserve(e.target);
          }
        });
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  // overflow-x: clip (not hidden) — hidden would create a scroll context
  // that silently disables the sticky header
  return (
    <div style={{ background: "var(--page-bg)", paddingBottom: 0, overflowX: "clip" }}>
      <Header />
      <Sections />
      <Footer />
    </div>
  );
}

const rootElement = document.getElementById("root");
if (rootElement) {
  createRoot(rootElement).render(<LandingPage />);
} else {
  // Document pages (/privacy, /terms, /about, /vision): mount the same
  // header and footer around the server-rendered article.
  const headerMount = document.getElementById("site-header");
  const footerMount = document.getElementById("site-footer");
  if (headerMount) createRoot(headerMount).render(<Header />);
  if (footerMount) createRoot(footerMount).render(<Footer />);
}
